import { atom, atomFamily } from 'recoil'
import { BusinessLineEnum } from 'constants/base'
import { MixContractInfo } from '../entity'

/**
 * websocket 推送tickersGroup字段精简结构
 * 可参考https://bglimited.larksuite.com/wiki/wikusrWmTh6HACqrM6K2okR3y7g?theme=FOLLOW_SYSTEM&contentTheme=DARK
 * 已稍微进行归类
 */
export type OriginalFutureTickerInfo = {
    // price 最新市场价
    p: string
    // indexPrice 指数价格
    iP: string
    // markPrice 合理标记价格
    mP: string
    // high 24H最高价
    h: string
    // low 24H最低价
    l: string
    // deliveryPrice 交割价格（仅交割合约）
    dP: string
    // bestAsk 最优卖价 卖一价
    bA: string
    // bestBid 最优买价 买一价
    bB: string
    // before24Open 24小时前开盘价，用于计算最近24H涨跌幅
    oB: string
    // change 涨跌幅(请勿直接使用)
    c: string
    // changeUtc UTC0涨跌幅（请勿直接使用）
    cU: string

    // symbolCode
    sC: string
    // symbolId
    sI: string
    // baseSymbol 左币，如BTC
    bS: string
    // quoteSymbol 计价币，如USDT
    qS: string
    // labelId 标签ID 1-New 2-Hot 3-ST
    lI: number

    // baseVolume 成交量(左币计量)
    bV: string
    // quoteVolume 成交额（右币计量）
    qV: string
    // usdtVolume 折合USDT成交量，注意：非USDT交易对，quoteVolume不等于usdtVolume
    uV: string
    // status 交易对状态 1-正常 0-下架 2-维护
    s: number
    // symbolType 1-永续 2-交割
    sT: number

    // settleFeeRateCycle 资金费率周期 8表示每8个小时
    cC: number
    // capitalRate 资金费率
    cR: string
    // nextSettleTime 下一个资金费用结算时间点（时间戳）
    nCT: string

    // systemTime 系统时间，主要用于计算倒计时
    t: string
    // openTime 开盘时间
    oT: string
    // deliveryTime 交割时间
    dT: string
    // deliveryStatusEnum 交割状态
    dS: number

    // holding 持仓量
    hV: number
    // showCountDown 是否显示倒计时 1-是
    sCD: number
}

/**
 * 合约Ticker信息
 * 语义化处理
 */
export type FutureTickerInfo = {
    // 最新市场价
    price: string
    // 指数价格
    indexPrice: string
    // 合理标记价格
    markPrice: string
    // 24H最高价
    high: string
    // 24H最低价
    low: string
    // 交割价格（仅交割合约）
    deliveryPrice: string
    // 最优卖价 卖一价
    bestAsk: string
    // 最优买价 买一价
    bestBid: string
    // 24小时前开盘价，用于计算最近24H涨跌幅
    before24Open: string
    // 涨跌幅(请勿直接使用)
    change: string
    // UTC0涨跌幅（请勿直接使用）
    changeUtc: string

    symbolCode: string
    symbolId: string
    // 左币，如BTC
    baseSymbol: string
    // 计价币，如USDT
    quoteSymbol: string
    // 标签ID 1-New 2-Hot 3-ST
    labelId: number

    // 成交量(左币计量)
    baseVolume: string
    // 成交额（右币计量）
    quoteVolume: string
    // 折合USDT成交量，注意：非USDT交易对，quoteVolume不等于usdtVolume
    usdtVolume: string
    // 交易对状态 1-正常 0-下架 2-维护
    status: number
    // 1-永续 2-交割
    symbolType: number

    // 资金费率周期 8表示每8个小时
    settleFeeRateCycle: number
    // 资金费率
    capitalRate: string
    // 下一个资金费用结算时间点（时间戳）
    nextSettleTime: string

    // 系统时间，主要用于计算倒计时
    systemTime: string
    // 开盘时间
    openTime: string

    // 业务线
    businessLine: number
}

export type CommonTicker = {
    baseSymbol: string // 左币：现货小写，合约大写
    baseVolume: string // 24小时成交量
    before24Open: string // 24小时前开盘价
    change: string // 涨跌幅
    chgUTC: string // UTC0时区涨跌幅
    high: string // 24小时最高价
    labelId: number // 标签id
    low: string // 24小时最低价
    openUtc: string // 暂不使用
    price: string // 最新价
    pricedSymbol: string // 计价币：现货大写，合约小写
    quoteVolume: string // 暂不使用
    symbolCode: string // 交易对code
    usdtVolume: string // 总交易额（USDT）
}

/**
 * 按照不同业务线组织的ticker列表
 */
export const TickerListAtom = atomFamily<CommonTicker[], BusinessLineEnum>({
    key: 'TickerListAtom',
    default: []
})
export type MinifiedTicker = Pick<
    FutureTickerInfo,
    'businessLine' | 'symbolCode' | 'symbolId' | 'markPrice' | 'price' | 'bestAsk' | 'bestBid'
>

export const MinifiedTickerListAtom = atom<MinifiedTicker[]>({
    key: 'futures-minifiedTickerList',
    default: []
})

export function createEmptyMinifiedTicker(): MinifiedTicker {
    return {
        bestAsk: '0',
        bestBid: '0',
        businessLine: 0,
        markPrice: '1',
        price: '0',
        symbolCode: '',
        symbolId: ''
    }
}

/**
 * 获取交易对ticker的工具函数
 * @param tickers
 * @param contractConfig
 */
export function getTicker(tickers: MinifiedTicker[], contractConfig: MixContractInfo) {
    let defaultTicker = createEmptyMinifiedTicker()
    if (!contractConfig) return defaultTicker
    return (
        tickers.find(
            t =>
                t.businessLine === contractConfig.businessLine &&
                t.symbolCode === contractConfig.symbolCode
        ) || defaultTicker
    )
}
